import { getAdminUrl, getMergedHeaders, useReapitConnectSession } from '@reapitform/hooks'

/**
 * Imperative admin-API delete helpers for the builder. The target reference IDs are only known at click time
 * (per row/card/section), so these can't use the fixed-path `useAdminUpdate` hook. A 409 response (e.g. a card
 * or section that still has children) surfaces the server's message so the UI can explain what to remove first.
 */
export const useBuilderDeletes = () => {
  const session = useReapitConnectSession()

  const del = async (path: string): Promise<void> => {
    const headers = await getMergedHeaders(session)
    if (!headers) throw new Error('Your session has expired — please sign in again.')

    const response = await fetch(getAdminUrl(path)(), {
      method: 'DELETE',
      headers: headers as HeadersInit,
    })

    if (!response.ok) {
      let message = `Delete failed (${response.status})`
      try {
        const body = await response.json()
        message = body?.description || body?.Description || message
      } catch {
        // Non-JSON body — keep the status-based message.
      }
      throw new Error(message)
    }
  }

  return {
    deleteField: (sectionReferenceID: string, fieldReferenceID: string) =>
      del(`/api/review-card-sections/${sectionReferenceID}/fields/${fieldReferenceID}`),

    deleteSection: (reviewCardReferenceID: string, sectionReferenceID: string) =>
      del(`/api/review-cards/${reviewCardReferenceID}/sections/${sectionReferenceID}`),

    deleteCard: (formReferenceID: string, versionReferenceID: string, reviewCardReferenceID: string) =>
      del(`/api/forms/${formReferenceID}/versions/${versionReferenceID}/review-cards/${reviewCardReferenceID}`),

    deletePdfTemplate: (formReferenceID: string, versionReferenceID: string, pdfTemplateReferenceID: string) =>
      del(`/api/forms/${formReferenceID}/versions/${versionReferenceID}/pdf-templates/${pdfTemplateReferenceID}`),

    deleteRecipientField: (recipientCardReferenceID: string, recipientFieldReferenceID: string) =>
      del(`/api/recipient-cards/${recipientCardReferenceID}/fields/${recipientFieldReferenceID}`),

    deleteRecipientCard: (recipientStepReferenceID: string, recipientCardReferenceID: string) =>
      del(`/api/recipient-steps/${recipientStepReferenceID}/cards/${recipientCardReferenceID}`),

    deleteRecipientStep: (formReferenceID: string, versionReferenceID: string, recipientStepReferenceID: string) =>
      del(`/api/forms/${formReferenceID}/versions/${versionReferenceID}/recipient-steps/${recipientStepReferenceID}`),

    deletePdfField: (formReferenceID: string, versionReferenceID: string, pdfFieldReferenceID: string) =>
      del(`/api/forms/${formReferenceID}/versions/${versionReferenceID}/pdf-fields/${pdfFieldReferenceID}`),

    deleteEventTemplate: (formReferenceID: string, versionReferenceID: string, eventTemplateReferenceID: string) =>
      del(`/api/forms/${formReferenceID}/versions/${versionReferenceID}/event-templates/${eventTemplateReferenceID}`),

    deleteActionTemplate: (formReferenceID: string, versionReferenceID: string, actionTemplateReferenceID: string) =>
      del(`/api/forms/${formReferenceID}/versions/${versionReferenceID}/action-templates/${actionTemplateReferenceID}`),

    deleteAllEventTemplates: (formReferenceID: string, versionReferenceID: string) =>
      del(`/api/forms/${formReferenceID}/versions/${versionReferenceID}/event-templates`),

    deleteAllActionTemplates: (formReferenceID: string, versionReferenceID: string) =>
      del(`/api/forms/${formReferenceID}/versions/${versionReferenceID}/action-templates`),

    deleteForm: (formReferenceID: string) => del(`/api/forms/${formReferenceID}`),

    deleteFormVersion: (formReferenceID: string, versionReferenceID: string) =>
      del(`/api/forms/${formReferenceID}/versions/${versionReferenceID}`),
  }
}
